import { useAutoAnimate } from '@formkit/auto-animate/react'
import React, { useEffect, useRef, useState } from 'react'
import Box from 'src/components/Box/Box'
import { useDropdownOpen } from 'src/hooks'
import { css } from 'styled-components'

interface PopupMenuProps {
  baseComponent: React.ReactElement
  baseComponentDropdownOpen?: React.ReactElement
  menuComponent: React.ReactElement
  dropdownOpenDebug?: boolean // this is purely for debugging purposes and should never be set
  topOffset?: number
  bottomOffset?: number
  leftOffset?: number
  rightOffset?: number
  autoWidth?: boolean
  width?: number
  paddingTop?: number
  paddingBottom?: number
  closeOnSecondClick?: boolean
  closeOnAnyClick?: boolean
}

const PopupMenu: React.FC<PopupMenuProps> = (props): React.ReactElement => {
  // TODO: maybe rename useDropdownOpen to useElementOpen?
  const { dropdownOpen, divRef, handleOpenerClick, setDropdownOpen } =
    useDropdownOpen({
      closeOnSecondClick: props.closeOnSecondClick ?? true,
      closeOnAnyClick: props.closeOnAnyClick ?? true,
    })
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const hoverTimer = useRef<NodeJS.Timeout | null>(null)
  const [baseComponentHover, _setBaseComponentHover] = useState(false)
  const [menuComponentHover, setMenuComponentHover] = useState(false)
  const setBaseComponentHover = (hovered: boolean): void => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current)
    }
    if (hovered) {
      _setBaseComponentHover(true)
    } else {
      hoverTimer.current = setTimeout(() => {
        _setBaseComponentHover(false)
      }, 100)
    }
  }

  const [menuComponentParent] = useAutoAnimate({
    duration: 150,
    easing: 'linear',
  })

  useEffect(() => {
    if (!baseComponentHover && !menuComponentHover) {
      setDropdownOpen(false)
    }
  }, [baseComponentHover, menuComponentHover])

  return (
    <div
      css={css`
        position: relative;
      `}
      data-isopen={dropdownOpen}
      ref={divRef}
      onMouseEnter={(): void => {
        setDropdownOpen(true)
        setBaseComponentHover(true)
      }}
      onMouseLeave={(): void => {
        setBaseComponentHover(false)
      }}
    >
      <div
        css={css`
          cursor: pointer;
          padding-top: ${props.paddingTop ?? 5}px;
          padding-bottom: ${props.paddingBottom ?? 0}px;
        `}
        onClick={(e): void => handleOpenerClick(e)}
        onKeyPress={(e): void => {
          if (e.key === 'Enter') {
            handleOpenerClick(e)
          }
        }}
        role="button"
        tabIndex={0}
        ref={ref}
      >
        {dropdownOpen ? (
          <>
            {props.baseComponentDropdownOpen
              ? props.baseComponentDropdownOpen
              : props.baseComponent}
          </>
        ) : (
          <>{props.baseComponent}</>
        )}
      </div>
      <div ref={menuComponentParent}>
        {(props.dropdownOpenDebug || dropdownOpen) && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            css={css`
              position: absolute;
              ${props.topOffset !== undefined
                ? `top: ${props.topOffset}px;`
                : ''}
              ${props.rightOffset !== undefined
                ? `right: ${props.rightOffset}px;`
                : ''}
              // Do not set bottom if top is set; do not set left if right is set
              ${props.topOffset !== undefined
                ? ''
                : `bottom: ${props.bottomOffset ?? 0}px;`}
              ${props.rightOffset !== undefined
                ? ''
                : `left: ${props.leftOffset ?? 0}px;`}
              z-index: 3;
            `}
            onMouseEnter={(): void => {
              setMenuComponentHover(true)
            }}
            onMouseLeave={(): void => {
              setMenuComponentHover(false)
            }}
            onFocus={(e): void => {
              if (!e.relatedTarget) {
                setMenuComponentHover(true)
              }
            }}
            onBlur={(e): void => {
              const menuElement = e.currentTarget
              const newFocusElement = e.relatedTarget as Node
              if (!menuElement.contains(newFocusElement)) {
                setMenuComponentHover(false)
              }
            }}
            onClick={(): void => {
              setDropdownOpen(false)
            }}
          >
            <Box
              css={css`
                min-width: ${props.autoWidth
                  ? 'auto'
                  : props.width
                    ? props.width + 'px'
                    : '250px'};
                overflow: hidden;
              `}
              noPadding
            >
              {props.menuComponent}
            </Box>
            {/* invisible hitbox */}
            {ref.current?.clientHeight && (
              <div
                css={css`
                  height: ${ref.current?.clientHeight}px;
                  opacity: 0;
                  cursor: pointer;
                `}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PopupMenu
